import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Share2, MessageCircle, X, Play, Calendar } from 'lucide-react';
import { AuthDialog } from '../AuthDialog';
import ImagesDev from '../../api/images';
import Link from '../Link';
import { getNextLiveSession } from '../../pages/Live';

export function WordOfTheDay_old() {
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authAction, setAuthAction] = useState(null);
  const [isLiked, setIsLiked] = useState(false);

  const handleAction = (action) => {
    // Check if user is logged in (implement your logic)
    const isLoggedIn = false;

    if (!isLoggedIn) {
      setAuthAction(action);
      setShowAuthDialog(true);
      return;
    }

    if (action === 'like') {
      setIsLiked(!isLiked);
    }
  };

  return (
    <section className="relative py-16 overflow-hidden">
    {/* <section className="relative py-16 bg-gradient-to-b from-gray-900 to-black overflow-hidden"> */}
      <div className="absolute inset-0">
        <div className={`absolute inset-0 opacity-10 bg-cover bg-center`} style={{background: `url(${ImagesDev.heroImagesDesk[8]}) center`, backgroundSize: "cover"}}></div>
        {/* <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1516450360452-9312f5e86fc7')] opacity-10 bg-cover bg-center"></div> */}
        {/* <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/90 backdrop-blur-sm"></div> */}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Word of the Day</h2>
            <div className="h-1 w-24 bg-gradient-to-r from-orange-400 to-amber-400 mx-auto"></div>
          </div>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
            {/* Memory Verse Bubble */}
            <div className="relative mb-8">
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-600 to-pink-600 rounded-2xl blur opacity-25"></div>
              <div className="relative bg-black/40 rounded-xl p-6 text-center">
                <p className="text-xl md:text-2xl text-white italic mb-4">
                  "For God has not given us a spirit of fear, but of power and of love and of a sound mind."
                </p>
                <p className="text-orange-300 font-semibold">2 Timothy 1:7</p>
              </div>
            </div>

            {/* Main Message */}
            <div className="space-y-6 text-gray-300">
              <p className="text-lg leading-relaxed">
                Fear is not your portion. Today, embrace the spirit of power, love, and sound judgment that God has given you. Let this truth transform your perspective and guide your actions.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                {['Power', 'Love', 'Sound Mind'].map((aspect, index) => (
                  <div key={index} className="bg-white/5 rounded-lg p-4 text-center">
                    <h3 className="text-xl font-semibold text-white mb-2">{aspect}</h3>
                    <p className="text-sm text-gray-400">
                      {aspect === 'Power' && "Strength to overcome any challenge"}
                      {aspect === 'Love' && "Perfect love that casts out all fear"}
                      {aspect === 'Sound Mind' && "Clarity and divine wisdom"}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
              <div className="flex items-center space-x-6">
                <button
                  onClick={() => handleAction('like')}
                  className={`flex items-center space-x-2 transition-colors ${
                    isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
                  }`}
                >
                  <Heart className={`w-6 h-6 ${isLiked ? 'fill-current' : ''}`} />
                  <span>247</span>
                </button>
                <button
                  onClick={() => handleAction('comment')}
                  className="flex items-center space-x-2 text-gray-400 hover:text-amber-400 transition-colors"
                >
                  <MessageCircle className="w-6 h-6" />
                  <span>42</span>
                </button>
              </div>
              <button className="flex items-center space-x-2 text-gray-400 hover:text-blue-400 transition-colors">
                <Share2 className="w-6 h-6" />
                <span>Share</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <AnimatePresence>
        {showAuthDialog && (
          <AuthDialog
            action={authAction}
            onClose={() => {
              setShowAuthDialog(false);
              setAuthAction(null);
            }}
          />
        )}
      </AnimatePresence>
    </section>
  );
}


export function WordOfTheDay() {
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [authAction, setAuthAction] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [liveSession, setLiveSession] = useState(null);

  useEffect(() => {
    const session = getNextLiveSession();
    setLiveSession(session);
  }, []);

  const handleAction = (action) => {
    const isLoggedIn = false; // 🔐 Replace with real auth check

    if (!isLoggedIn) {
      setAuthAction(action);
      setShowAuthDialog(true);
      return;
    }

    if (action === "like") {
      setIsLiked(!isLiked);
    }
  };

  if (!liveSession) return null;

  const { isLive, scheduledTime, title } = liveSession;
  const dateStr = new Date(scheduledTime).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const timeStr = new Date(scheduledTime).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <section className="relative py-8 md:py-16 bg-gradient-to-b from-gray-900 to-black overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1516450360452-9312f5e86fc7')] opacity-10 bg-cover bg-center"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/90 backdrop-blur-sm"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {isLive ? (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-0 animate-pulse"
          >
            <div className="bg-red-600/20 border border-red-500/30 rounded-2xl p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-sm md:text-xl text-red-400 font-bold">LIVE NOW</span>
                  </div>
                  <h3 className="text-sm md:text-xl font-bold text-white">Bible Study in Progress</h3>
                </div>
                <Link
                  to="/live"
                  className="text-[11px] md:text-lg flex items-center gap-2 bg-red-600 hover:bg-red-700 px-6 py-3 rounded-full text-white font-medium transition-colors"
                >
                  <Play className="w-5 h-5" />
                  <span>Join Live</span>
                </Link>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-0"
          >
            <div className="bg-amber-600/20 border border-amber-500/30 rounded-2xl p-6 backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm md:text-xl font-bold text-white mb-2">Next Bible Study</h3>
                  <p className="text-[10px] md:text-lg text-amber-300 mb-1">{title}</p>
                  <div className="text-[10px] md:text-sm flex items-center gap-4 text-gray-400">
                    <span>{dateStr}</span>
                    <span>•</span>
                    <span>{timeStr}</span>
                  </div>
                </div>
                <Link
                  to="/live"
                  className="text-[11px] md:text-lg flex items-center gap-2 bg-amber-600 hover:bg-amber-700 px-6 py-3 rounded-full text-white font-medium transition-colors"
                >
                  <Calendar className="w-5 h-5" />
                  <span>Set Reminder</span>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <AnimatePresence>
        {showAuthDialog && (
          <AuthDialog
            action={authAction}
            onClose={() => {
              setShowAuthDialog(false);
              setAuthAction(null);
            }}
          />
        )}
      </AnimatePresence>
    </section>
  );
}



// const Nothing = () => {
//   return(
//     <motion.div
//           initial={{ opacity: 0, y: 20 }}
//           whileInView={{ opacity: 1, y: 0 }}
//           viewport={{ once: true }}
//           className="max-w-4xl mx-auto"
//         >
//           <div className="text-center mb-12">
//             <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Word of the Day</h2>
//             <div className="h-1 w-24 bg-gradient-to-r from-amber-400 to-pink-400 mx-auto"></div>
//           </div>

//           <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
//             {/* Memory Verse Bubble */}
//             <div className="relative mb-8">
//               <div className="absolute -inset-1 bg-gradient-to-r from-amber-600 to-pink-600 rounded-2xl blur opacity-25"></div>
//               <div className="relative bg-black/40 rounded-xl p-6 text-center">
//                 <p className="text-xl md:text-2xl text-white italic mb-4">
//                   "For God has not given us a spirit of fear, but of power and of love and of a sound mind."
//                 </p>
//                 <p className="text-amber-300 font-semibold">2 Timothy 1:7</p>
//               </div>
//             </div>

//             {/* Main Message */}
//             <div className="space-y-6 text-gray-300">
//               <p className="text-lg leading-relaxed">
//                 Fear is not your portion. Today, embrace the spirit of power, love, and sound judgment that God has given you. Let this truth transform your perspective and guide your actions.
//               </p>
              
//               <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
//                 {['Power', 'Love', 'Sound Mind'].map((aspect, index) => (
//                   <div key={index} className="bg-white/5 rounded-lg p-4 text-center">
//                     <h3 className="text-xl font-semibold text-white mb-2">{aspect}</h3>
//                     <p className="text-sm text-gray-400">
//                       {aspect === 'Power' && "Strength to overcome any challenge"}
//                       {aspect === 'Love' && "Perfect love that casts out all fear"}
//                       {aspect === 'Sound Mind' && "Clarity and divine wisdom"}
//                     </p>
//                   </div>
//                 ))}
//               </div>
//             </div>

//             {/* Action Buttons */}
//             <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
//               <div className="flex items-center space-x-6">
//                 <button
//                   onClick={() => handleAction('like')}
//                   className={`flex items-center space-x-2 transition-colors ${
//                     isLiked ? 'text-red-500' : 'text-gray-400 hover:text-red-500'
//                   }`}
//                 >
//                   <Heart className={`w-6 h-6 ${isLiked ? 'fill-current' : ''}`} />
//                   <span>247</span>
//                 </button>
//                 <button
//                   onClick={() => handleAction('comment')}
//                   className="flex items-center space-x-2 text-gray-400 hover:text-amber-400 transition-colors"
//                 >
//                   <MessageCircle className="w-6 h-6" />
//                   <span>42</span>
//                 </button>
//               </div>
//               <button className="flex items-center space-x-2 text-gray-400 hover:text-blue-400 transition-colors">
//                 <Share2 className="w-6 h-6" />
//                 <span>Share</span>
//               </button>
//             </div>
//           </div>
//         </motion.div>
//   )
// }